/**
 * JobRoleFamily API Contract
 *
 * Mirrors: backend/src/routes/jobRoleFamily.routes.ts
 * 1:1 parity with existing Express routes
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { uuidSchema } from "@hourly/api/validation-common";
import { JobRoleFamilyDetailResponseSchema, CreateJobRoleFamilyInputSchema, UpdateJobRoleFamilyInputSchema, UpdateJobRoleFamilySkillsInputSchema, AddJobRoleFamilySkillInputSchema, ArchiveJobRoleFamilyInputSchema, } from "@hourly/api/jobRoleFamily";
// ============================================
// JobRoleFamily Contracts
// ============================================
/**
 * POST /job-role-families
 * Create a new job role family
 */
export const create = oc
    .route({
    method: "POST",
    path: "/job-role-families",
    summary: "Create a new job role family",
    tags: ["JobRoleFamilies"],
    successStatus: 201,
})
    .input(CreateJobRoleFamilyInputSchema)
    .output(JobRoleFamilyDetailResponseSchema);
/**
 * GET /job-role-families/:id
 * Get a job role family by ID
 */
export const getById = oc
    .route({
    method: "GET",
    path: "/job-role-families/{id}",
    summary: "Get job role family by ID",
    tags: ["JobRoleFamilies"],
})
    .input(z.object({ id: uuidSchema }))
    .output(JobRoleFamilyDetailResponseSchema);
/**
 * GET /job-role-families
 * Get all job role families for the authenticated user's company
 */
export const listByCompany = oc
    .route({
    method: "GET",
    path: "/job-role-families",
    summary: "List job role families for a company",
    tags: ["JobRoleFamilies"],
})
    .input(z.object({
    includeArchived: z
        .string()
        .optional()
        .transform((val) => val === "true"),
}))
    .output(z.array(JobRoleFamilyDetailResponseSchema));
/**
 * PATCH /job-role-families/:id
 * Update a job role family
 */
export const update = oc
    .route({
    method: "PATCH",
    path: "/job-role-families/{id}",
    summary: "Update a job role family",
    tags: ["JobRoleFamilies"],
})
    .input(z.object({ id: uuidSchema }).merge(UpdateJobRoleFamilyInputSchema))
    .output(JobRoleFamilyDetailResponseSchema);
/**
 * DELETE /job-role-families/:id
 * Delete a job role family
 */
export const remove = oc
    .route({
    method: "DELETE",
    path: "/job-role-families/{id}",
    summary: "Delete a job role family",
    tags: ["JobRoleFamilies"],
    successStatus: 204,
})
    .input(z.object({ id: uuidSchema }))
    .output(z.void());
/**
 * PATCH /job-role-families/:id/skills
 * Update skills array for a job role family
 */
export const updateSkills = oc
    .route({
    method: "PATCH",
    path: "/job-role-families/{id}/skills",
    summary: "Update skills array for a job role family",
    tags: ["JobRoleFamilies"],
})
    .input(z.object({ id: uuidSchema }).merge(UpdateJobRoleFamilySkillsInputSchema))
    .output(JobRoleFamilyDetailResponseSchema);
/**
 * POST /job-role-families/:id/skills
 * Add a single skill to a job role family
 */
export const addSkill = oc
    .route({
    method: "POST",
    path: "/job-role-families/{id}/skills",
    summary: "Add a single skill to a job role family",
    tags: ["JobRoleFamilies"],
})
    .input(z.object({ id: uuidSchema }).merge(AddJobRoleFamilySkillInputSchema))
    .output(JobRoleFamilyDetailResponseSchema);
/**
 * DELETE /job-role-families/:id/skills/:skillId
 * Remove a skill from a job role family
 */
export const removeSkill = oc
    .route({
    method: "DELETE",
    path: "/job-role-families/{id}/skills/{skillId}",
    summary: "Remove a skill from a job role family",
    tags: ["JobRoleFamilies"],
})
    .input(z.object({ id: uuidSchema, skillId: uuidSchema }))
    .output(JobRoleFamilyDetailResponseSchema);
/**
 * POST /job-role-families/:id/archive
 * Archive a job role family
 */
export const archive = oc
    .route({
    method: "POST",
    path: "/job-role-families/{id}/archive",
    summary: "Archive a job role family",
    tags: ["JobRoleFamilies"],
})
    .input(z.object({ id: uuidSchema }).merge(ArchiveJobRoleFamilyInputSchema))
    .output(JobRoleFamilyDetailResponseSchema);
/**
 * POST /job-role-families/:id/unarchive
 * Unarchive a job role family
 */
export const unarchive = oc
    .route({
    method: "POST",
    path: "/job-role-families/{id}/unarchive",
    summary: "Unarchive a job role family",
    tags: ["JobRoleFamilies"],
})
    .input(z.object({ id: uuidSchema }).merge(ArchiveJobRoleFamilyInputSchema))
    .output(JobRoleFamilyDetailResponseSchema);
// ============================================
// Contract Router
// ============================================
export const jobRoleFamilyContract = {
    create,
    getById,
    listByCompany,
    update,
    remove,
    updateSkills,
    addSkill,
    removeSkill,
    archive,
    unarchive,
};
//# sourceMappingURL=jobRoleFamily.js.map